'use client';

import {
  homeSearchSampleGroups,
  type HomeSearchSampleIcon,
} from '@/app/(public)/(home)/home-search-samples-data';
import { cn } from '@/lib/utils';
import {
  BookOpenIcon,
  ChevronDownIcon,
  CircuitBoardIcon,
  ClockIcon,
  FileSearchIcon,
  GalleryVerticalEndIcon,
  LightbulbIcon,
  type LucideIcon,
} from 'lucide-react';
import { useMemo, useState } from 'react';

const sampleIconMap: Record<HomeSearchSampleIcon, LucideIcon> = {
  filter: ClockIcon,
  evidence: FileSearchIcon,
  gap: LightbulbIcon,
  presentation: BookOpenIcon,
  compare: CircuitBoardIcon,
  interdisciplinary: GalleryVerticalEndIcon,
};

const sampleGroups = homeSearchSampleGroups;

const SAMPLE_VISIBILITY_STORAGE_KEY = 'suppr.home.searchSamplesHidden';

interface HomeSearchSamplesProps {
  onSelectQuery: (query: string) => void;
}

export default function HomeSearchSamples({
  onSelectQuery,
}: HomeSearchSamplesProps) {
  const [activeType, setActiveType] = useState(sampleGroups[0]?.type ?? '');
  const [hidden, setHidden] = useState(() =>
    typeof window === 'undefined'
      ? false
      : window.localStorage.getItem(SAMPLE_VISIBILITY_STORAGE_KEY) === '1',
  );

  const updateHidden = (nextHidden: boolean) => {
    setHidden(nextHidden);
    window.localStorage.setItem(
      SAMPLE_VISIBILITY_STORAGE_KEY,
      nextHidden ? '1' : '0',
    );
  };

  const activeGroup = useMemo(
    () =>
      sampleGroups.find((group) => group.type === activeType) ??
      sampleGroups[0],
    [activeType],
  );

  if (!activeGroup) {
    return null;
  }

  return (
    <div className="w-full max-w-196 mx-auto mt-3">
      {hidden ? (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={() => updateHidden(false)}
            className="px-4 py-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            显示建议
          </button>
        </div>
      ) : (
        <>
          <div className="relative">
            <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-6 bg-linear-to-r from-background to-transparent" />
            <div className="pointer-events-none absolute inset-y-0 right-4 z-10 w-12 bg-linear-to-l from-background via-background/90 to-transparent" />
            <div className="flex items-center gap-1">
              <div className="min-w-0 flex-1 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                <div className="flex min-w-max items-center gap-1.5 px-1 pr-3">
                  {sampleGroups.map((group) => {
                    const Icon = sampleIconMap[group.icon];
                    const active = group.type === activeType;

                    return (
                      <button
                        key={group.type}
                        type="button"
                        onClick={() => setActiveType(group.type)}
                        className={cn(
                          'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition-colors',
                          active
                            ? 'border-transparent bg-muted text-foreground'
                            : 'border-border bg-background text-muted-foreground hover:text-foreground',
                        )}
                      >
                        <Icon className="h-3.5 w-3.5" />
                        <span className="whitespace-nowrap font-medium">
                          {group.type}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <button
                type="button"
                aria-label="收起搜索用例"
                onClick={() => updateHidden(true)}
                className="relative z-20 mb-1 inline-flex h-8 w-8 shrink-0 items-center justify-center bg-transparent text-muted-foreground transition-colors hover:text-foreground"
              >
                <ChevronDownIcon className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="border rounded-2xl bg-card py-2 mt-2 px-2">
            {activeGroup.items.map((item) => (
              <button
                key={item.query}
                type="button"
                onClick={() => onSelectQuery(item.query)}
                className="block w-full rounded-xl px-3 py-1.5 text-left text-sm leading-6 text-foreground/85 transition-colors hover:bg-muted hover:text-foreground"
              >
                {item.query}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
